import React, { useState, useEffect, useRef } from "react";
import Categories from "./Categories";
import Products from "./ProductList";
import Filters from "./Filters";
import {
  Product,
  CategoryProductStore,
  FileReturn,
  FileInstance,
} from "../../Interfaces/Product";
import "./Storefront.css";
import "./Filters.css";
import { FundraiserParticipantShop } from "../../Interfaces/FundraiserRole";
import config from "../../config";
import FileDisplay from "./ProductFileList";
const BASE_API = config.API_BASE_URL;

interface StorefrontProps {
  fundraiserId: number;
  participant: FundraiserParticipantShop;
}

const Storefront = ({ fundraiserId, participant }: StorefrontProps) => {
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryProductStore | null>(null);
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({});
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [categories, setCategories] = useState<CategoryProductStore[]>([]);
  const [designFiles, setDesignFiles] = useState<FileReturn[]>([]);
  const [bottleDesignFiles, setBottleDesignFiles] = useState<FileReturn[]>([]);
  const [clothingDesignFiles, setClothingDesignFiles] = useState<FileReturn[]>(
    []
  );

  const productsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(
          `${BASE_API}fundraiser/${fundraiserId}/categories/products`
        );
        if (!response.ok) throw new Error("Failed to fetch products");
        const data: CategoryProductStore[] = await response.json();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    const fetchDesignFiles = async () => {
      try {
        const response = await fetch(
          `${BASE_API}files/fundraiser/${fundraiserId}/type/bottle/`
        );
        if (!response.ok) throw new Error("Failed to fetch bottle files");
        const data: FileReturn[] = await response.json();
        setBottleDesignFiles(data);
      } catch (error) {
        console.error("Error fetching bottle files:", error);
      }
      try {
        const response = await fetch(
          `${BASE_API}files/fundraiser/${fundraiserId}/type/clothing/`
        );
        if (!response.ok) throw new Error("Failed to fetch clothing files");
        const data: FileReturn[] = await response.json();
        setClothingDesignFiles(data);
      } catch (error) {
        console.error("Error fetching clothing files:", error);
      }
    };

    if (fundraiserId) {
      fetchDesignFiles();
      fetchProducts();
    }
  }, [fundraiserId]);

  useEffect(() => {
    if (categories.length > 0) {
      const bottleCategory = categories.find(
        (category) => category.category === "bottle"
      );
      const defaultCategory = bottleCategory || categories[0];
      setSelectedCategory(defaultCategory);

      if (bottleCategory) {
        setDesignFiles(bottleDesignFiles);
        updateFilters(bottleDesignFiles);
      } else {
        setDesignFiles([]);
      }
    }
  }, [categories, bottleDesignFiles]);

  const updateFilters = (bottleFiles: FileReturn[]) => {
    let categories: { [key: string]: string[] } = {};

    bottleFiles.forEach(({ category, sub_category }) => {
      if (!categories[category]) {
        categories[category] = [];
      }
      if (
        !categories[category].includes(sub_category) &&
        sub_category !== "Uncategorized"
      ) {
        categories[category].push(sub_category);
      }
    });

    setFilters(categories);
  };
  const designTableIsActive = selectedCategory?.has_design_filters;

  const handleCategorySelect = (category: CategoryProductStore) => {
    if (selectedCategory !== category) {
      setSelectedCategory(category);
      setActiveFilters([]);
      updateFilters(designFiles);
    }
    if (category.category === "bottle") {
      setDesignFiles(bottleDesignFiles);
    }
    if (category.category === "shirt" || category.category === "hoodie") {
      setDesignFiles(clothingDesignFiles);
    }

    const productSection = document.getElementById("product-section");
    if (productSection) {
      productSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleFilterChange = (filter: string, isChecked: boolean) => {
    if (isChecked) {
      setActiveFilters((prev) => [...prev, filter]);
    } else {
      setActiveFilters((prev) => prev.filter((f) => f !== filter));
    }
  };

  const filteredProducts =
    categories
      .find((category) => category.category === selectedCategory?.category)
      ?.products.filter((product) =>
        activeFilters.every((filter) =>
          product.item_description?.includes(filter)
        )
      ) || [];

  const filteredDesigns = designFiles.flatMap(
    ({ files, category, sub_category }) => {
      if (activeFilters.length === 0) {
        return files;
      }
      if (
        activeFilters.some(
          (filter) => filter === category || filter === sub_category
        )
      ) {
        return files;
      }
      return [];
    }
  );

  return (
    <div className="storefront">
      {categories.length === 0 ? (
        <div className="loading">Loading categories...</div>
      ) : (
        <>
          <div className="header">Start Shopping</div>
          <Categories
            categories={categories}
            onSelectCategory={handleCategorySelect}
            selectedCategoryName={selectedCategory?.category || ""}
          />

          <hr className="category-product-divider" />

          {selectedCategory && (
            <div className="filters-products-container" id="product-section">
              {designTableIsActive && (
                <Filters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
              )}
              {designTableIsActive && (
                <FileDisplay
                  files={filteredDesigns}
                  participant={participant}
                />
              )}
              {!designTableIsActive && (
                <Products
                  products={filteredProducts}
                  participant={participant}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Storefront;
